// Alertes Emploi - Frontend connecté au Backend
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Plus, Trash2, Edit, MapPin, Briefcase, Euro, Clock, BellOff, Check, X, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Modal de création/modification d'alerte
function AlerteFormModal({ isOpen, onClose, onSave, editingAlerte, isLoading }) {
  const [formData, setFormData] = useState({
    name: '',
    keywords: '',
    location: '',
    salary: '',
    frequency: 'Quotidienne',
  });

  // Mettre à jour le formulaire quand on édite une alerte existante
  useEffect(() => {
    if (editingAlerte) {
      setFormData({
        name: editingAlerte.name || '',
        keywords: editingAlerte.keywords || '',
        location: editingAlerte.location || '',
        salary: editingAlerte.salary || '',
        frequency: editingAlerte.frequency || 'Quotidienne',
      });
    } else {
      setFormData({
        name: '',
        keywords: '',
        location: '',
        salary: '',
        frequency: 'Quotidienne',
      });
    }
  }, [editingAlerte, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Le nom de l\'alerte est requis');
      return;
    }
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 md:left-[288px] bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl w-full max-w-md shadow-xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">
              {editingAlerte ? 'Modifier l\'alerte' : 'Créer une alerte'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" disabled={isLoading}>
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Nom de l'alerte *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Plombier Lyon"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Mots-clés / Métiers
              </label>
              <input
                type="text"
                value={formData.keywords}
                onChange={(e) => setFormData(prev => ({ ...prev, keywords: e.target.value }))}
                placeholder="Ex: Plombier, Chauffagiste"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                disabled={isLoading}
              />
              <p className="text-xs text-gray-400 mt-1">Séparez les mots-clés par des virgules</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Localisation
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Ex: Lyon et 30km"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Salaire minimum
              </label>
              <input
                type="text"
                value={formData.salary}
                onChange={(e) => setFormData(prev => ({ ...prev, salary: e.target.value }))}
                placeholder="Ex: 2500€+"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Fréquence des notifications
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData(prev => ({ ...prev, frequency: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                disabled={isLoading}
              >
                <option value="Instantanée">Instantanée</option>
                <option value="Quotidienne">Quotidienne</option>
                <option value="Hebdomadaire">Hebdomadaire</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={isLoading}>
                Annuler
              </Button>
              <Button type="submit" className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white" disabled={isLoading}>
                {isLoading ? (
                  <><Loader2 size={16} className="animate-spin mr-2" /> Enregistrement...</>
                ) : (
                  editingAlerte ? 'Enregistrer' : 'Créer l\'alerte'
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function AlerteCard({ alerte, onToggle, onEdit, onDelete, isToggling }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl border p-4 ${alerte.active ? 'border-cyan-200' : 'border-gray-200'}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          alerte.active ? 'bg-cyan-100' : 'bg-gray-100'
        }`}>
          {alerte.active ? (
            <Bell size={20} className="text-cyan-600" />
          ) : (
            <BellOff size={20} className="text-gray-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{alerte.name}</h3>
          
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
            {alerte.keywords && (
              <span className="flex items-center gap-1">
                <Briefcase size={12} />
                {alerte.keywords}
              </span>
            )}
            {alerte.location && (
              <span className="flex items-center gap-1">
                <MapPin size={12} />
                {alerte.location}
              </span>
            )}
            {alerte.salary && (
              <span className="flex items-center gap-1">
                <Euro size={12} />
                {alerte.salary}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
            <Clock size={12} />
            <span>Fréquence : {alerte.frequency}</span>
            <span>•</span>
            <span className="text-cyan-600 font-medium">{alerte.match_count || 0} offres correspondantes</span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={() => onToggle(alerte.id)}
            disabled={isToggling}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              alerte.active 
                ? 'bg-cyan-100 text-cyan-700' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            } ${isToggling ? 'opacity-50' : ''}`}
          >
            {alerte.active ? 'Active' : 'Inactive'}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
        <Button variant="outline" size="sm" className="flex-1 text-xs h-8" onClick={() => onEdit(alerte)}>
          <Edit size={12} className="mr-1" /> Modifier
        </Button>
        <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 h-8" onClick={() => onDelete(alerte.id)}>
          <Trash2 size={12} />
        </Button>
      </div>
    </motion.div>
  );
}

export default function AlertesEmploi() {
  const { user } = useCurrentUser();
  const [showForm, setShowForm] = useState(false);
  const [editingAlerte, setEditingAlerte] = useState(null);
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  // Charger les alertes au montage
  useEffect(() => {
    if (user?.id) {
      loadAlertes();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadAlertes = async () => {
    try {
      setLoading(true);
      const userId = user?.id || 'demo-user';
      const res = await fetch(`${API_URL}/api/alertes-emploi/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setAlertes(data);
      }
    } catch (err) {
      console.error('Erreur chargement alertes:', err);
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      setTogglingId(id);
      const res = await fetch(`${API_URL}/api/alertes-emploi/${id}/toggle`, {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        setAlertes(prev => prev.map(a => 
          a.id === id ? { ...a, active: data.active } : a
        ));
        toast.success('Alerte mise à jour');
      }
    } catch (err) {
      console.error('Erreur toggle:', err);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setTogglingId(null);
    }
  };

  const handleEdit = (alerte) => {
    setEditingAlerte(alerte);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette alerte ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/alertes-emploi/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setAlertes(prev => prev.filter(a => a.id !== id));
        toast.success('Alerte supprimée');
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSave = async (formData) => {
    try {
      setSaving(true);
      const userId = user?.id || 'demo-user';
      
      if (editingAlerte) {
        // Modification
        const res = await fetch(`${API_URL}/api/alertes-emploi/${editingAlerte.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (res.ok) {
          const updated = await res.json();
          setAlertes(prev => prev.map(a => a.id === editingAlerte.id ? updated : a));
          toast.success('Alerte modifiée');
        }
      } else {
        // Création
        const res = await fetch(`${API_URL}/api/alertes-emploi`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, user_id: userId }),
        });
        if (res.ok) {
          const newAlerte = await res.json();
          setAlertes(prev => [newAlerte, ...prev]);
          toast.success('Alerte créée');
        }
      }
      
      setShowForm(false);
      setEditingAlerte(null);
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseForm = () => {
    if (!saving) {
      setShowForm(false);
      setEditingAlerte(null);
    }
  };

  const activeCount = alertes.filter(a => a.active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={32} className="animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Alertes Emploi</h1>
          <p className="text-sm text-gray-500">{activeCount} alerte{activeCount > 1 ? 's' : ''} active{activeCount > 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-cyan-500 hover:bg-cyan-600 text-white">
          <Plus size={16} className="mr-2" /> Créer une alerte
        </Button>
      </div>

      {/* Info */}
      <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-100">
        <div className="flex items-start gap-3">
          <Check size={18} className="text-cyan-600 mt-0.5" />
          <div>
            <p className="text-xs text-cyan-800">
              Les alertes te notifient automatiquement quand de nouvelles offres correspondent à tes critères. 
              Le nombre d'offres correspondantes est calculé en temps réel.
            </p>
          </div>
        </div>
      </div>

      {alertes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Bell size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Aucune alerte</h3>
          <p className="text-xs text-gray-500 mb-4">Crée une alerte pour être notifié des nouvelles offres</p>
          <Button onClick={() => setShowForm(true)} className="bg-cyan-500 hover:bg-cyan-600 text-white">
            Créer ma première alerte
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {alertes.map(alerte => (
            <AlerteCard
              key={alerte.id}
              alerte={alerte}
              onToggle={handleToggle}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isToggling={togglingId === alerte.id}
            />
          ))}
        </div>
      )}

      {/* Modal de création/modification */}
      <AlerteFormModal
        isOpen={showForm}
        onClose={handleCloseForm}
        onSave={handleSave}
        editingAlerte={editingAlerte}
        isLoading={saving}
      />
    </div>
  );
}
